// Phase 5 — Show Data drawer support.
// Fetches the full audit chain (tool calls + replay metadata) for a
// consultant message. Used by the Show Data drawer to expose provenance
// details — raw tool inputs, raw tool outputs, latency, errors — that
// were captured by Phase 1's persistence layer.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ConsultantToolCallRow {
  id: string;
  message_id: string;
  conversation_id: string | null;
  tool_name: string;
  tool_input: Record<string, unknown> | null;
  raw_output: unknown;
  raw_input_resolved: Record<string, unknown> | null;
  tool_output_summary: Record<string, unknown> | null;
  latency_ms: number | null;
  error: string | null;
  created_at: string;
}

export interface ConsultantMessageMeta {
  id: string;
  query_hash: string | null;
  data_vintage: string | null;
  model_version: string | null;
  token_count: number | null;
  status: string | null;
}

/**
 * Hook that returns the tool-call audit chain + replay metadata for a
 * single consultant message. Returns null when message_id is null/absent
 * (e.g., the user-side bubble — only assistant messages have tool calls).
 *
 * The query is gated on `enabled` so we don't hit Supabase until the
 * drawer actually opens.
 */
export function useConsultantToolCalls(messageId: string | null, opts?: { enabled?: boolean }) {
  const enabled = (opts?.enabled ?? true) && !!messageId;

  return useQuery({
    queryKey: ["consultant-tool-calls", messageId],
    enabled,
    queryFn: async (): Promise<{
      message: ConsultantMessageMeta | null;
      toolCalls: ConsultantToolCallRow[];
    }> => {
      if (!messageId) return { message: null, toolCalls: [] };

      const [{ data: msgRow, error: msgErr }, { data: callRows, error: callErr }] = await Promise.all([
        supabase
          .from("consultant_messages")
          .select("id, query_hash, data_vintage, model_version, token_count, status")
          .eq("id", messageId)
          .maybeSingle(),
        supabase
          .from("consultant_tool_calls")
          .select("id, message_id, conversation_id, tool_name, tool_input, raw_output, raw_input_resolved, tool_output_summary, latency_ms, error, created_at")
          .eq("message_id", messageId)
          .order("created_at", { ascending: true }),
      ]);

      if (msgErr) throw new Error(`consultant_messages read: ${msgErr.message}`);
      if (callErr) throw new Error(`consultant_tool_calls read: ${callErr.message}`);

      return {
        message: (msgRow ?? null) as ConsultantMessageMeta | null,
        toolCalls: (callRows ?? []) as ConsultantToolCallRow[],
      };
    },
    staleTime: 5 * 60 * 1000, // 5 min — tool-call rows are append-only
  });
}
